/**
 * @module stores/useLocationStore
 * @description Zustand store for multi-location management in EmberPOS.
 * Persists the current location selection to localStorage. Fetches locations
 * from the PostgreSQL-backed API at /api/tenants/locations (the canonical,
 * authenticated, tenant-scoped surface — ticket 4556 deleted the old
 * unauthenticated /api/locations that used to sit alongside it). Dispatches
 * a 'location-changed' CustomEvent on the window when the active location changes.
 *
 * Demo/fallback locations are provided when the API is unavailable.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE_URL, isNativeApp } from '../config';
import { getCachedLocationLimit, canPurchase } from '../services/appStorePurchase';
import { getDeviceToken } from '../lib/authToken';

const LOCATIONS_URL = `${API_BASE_URL}/api/tenants/locations`;

// Every verb on /api/tenants/locations requires the owner (device) token.
function authHeaders() {
  const token = getDeviceToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// The canonical tenants.locations schema stores a structured address
// (address_line1/2, city, state, ...); this app's UI has only ever
// collected a single free-text "address" field. Adapt at the store
// boundary so LocationManagement.jsx's form/display never has to change.
function toApiPayload({ address, ...rest }) {
  return { ...rest, address_line1: address };
}
function fromApiLocation(loc) {
  return { ...loc, address: loc.address_line1 };
}

/** @type {Array<Object>} Fallback locations when the API is not reachable */
const FALLBACK_LOCATIONS = [
  {
    id: 1,
    name: 'Main Location',
    address: '100 Main St, Phoenix, AZ 85004',
    phone: '(602) 555-0100',
    timezone: 'America/Phoenix',
    is_active: true,
  },
];

/**
 * Zustand store for restaurant location state and actions.
 *
 * **State:**
 * - locations {Array<Object>} - All available locations (from API or fallback)
 * - currentLocationId {number} - ID of the currently selected location
 * - isLoading {boolean} - Whether locations are being fetched
 * - error {string|null} - Last error message, if any
 *
 * **Computed:**
 * - getCurrentLocation() - Returns the current location object (falls back to first)
 * - getActiveLocations() - Returns only active locations
 *
 * **Actions:**
 * - setCurrentLocation(locationId) - Switch to a different location
 * - fetchLocations() - Load locations from the API
 * - createLocation(data) - Create a new location via API
 * - updateLocation(id, data) - Update a location via API
 * - deleteLocation(id) - Soft-delete a location via API
 *
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<Object>>}
 */
export const useLocationStore = create(
  persist(
    (set, get) => ({
      // State
      locations: FALLBACK_LOCATIONS,
      currentLocationId: 1,
      isLoading: false,
      error: null,

      /**
       * Get the currently selected location object.
       * @returns {Object} Current location, or the first location as fallback
       */
      getCurrentLocation: () => {
        const { locations, currentLocationId } = get();
        return locations.find((loc) => loc.id === currentLocationId) || locations[0];
      },

      /**
       * Get all active (non-disabled) locations.
       * @returns {Array<Object>} Filtered array of active locations
       */
      getActiveLocations: () => {
        return get().locations.filter((loc) => loc.is_active !== false);
      },

      /**
       * Switch to a different location by ID.
       * Dispatches a 'location-changed' CustomEvent on the window.
       * @param {number} locationId - The location ID to switch to
       * @returns {void}
       */
      setCurrentLocation: (locationId) => {
        const { locations } = get();
        const location = locations.find((loc) => loc.id === locationId);
        if (location) {
          set({ currentLocationId: locationId });
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('location-changed', { detail: { locationId, location } })
            );
          }
        }
      },

      /**
       * Fetch locations from the server API.
       * Falls back to FALLBACK_LOCATIONS if the API call fails.
       * @returns {Promise<void>}
       */
      fetchLocations: async () => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(LOCATIONS_URL, { headers: authHeaders() });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const body = await res.json();
          const data = Array.isArray(body?.locations) ? body.locations.map(fromApiLocation) : [];

          if (data.length > 0) {
            // Never switch the data scope implicitly (ticket 9095). The
            // X-Location-Id every apiFetch sends is currentLocationId, but a
            // tenant's tables/menu/orders carry the legacy location_id they
            // were copied with (ember_copy_sample_tables), not the id of the
            // tenants.locations row returned here. Auto-switching to data[0]
            // made the floor plan's next poll return [] on iPad (the desktop
            // TableMap header mounts LocationSwitcher, which calls this).
            // Only an explicit setCurrentLocation changes scope.
            set({ locations: data, isLoading: false });
          } else {
            set({ locations: FALLBACK_LOCATIONS, isLoading: false });
          }
        } catch (error) {
          console.error('[Locations] Fetch failed:', error);
          set({ error: error.message, isLoading: false });
          // Keep existing locations in store; only use fallback if store is empty
          const { locations } = get();
          if (!locations || locations.length === 0) {
            set({ locations: FALLBACK_LOCATIONS });
          }
        }
      },

      /**
       * Create a new location via API.
       * @param {Object} data - { name, address, phone, timezone }
       * @returns {Promise<Object|null>} The created location or null on failure
       */
      createLocation: async (data) => {
        // Gates on the tenant's REAL per-tier location_limit (cached via
        // setCachedLocationLimit, set from /me's tenant.locationLimit) —
        // ticket 4556 finding: the old gate only asked "is this tenant on
        // any paid tier at all", which let a Starter tenant (limit 1) add
        // unlimited locations the same as Enterprise. null/-1 (cached as
        // null) means unlimited/unknown — don't block. canPurchase() guards
        // against a native build with no working buy path (non-MAS Electron
        // dmg/zip, plain iOS wrapper shell) locking a feature nobody there
        // can unlock — stay uncapped instead. This is still advisory only:
        // the server route (POST /api/tenants/locations) enforces the real
        // limit atomically.
        if (isNativeApp && canPurchase()) {
          const limit = getCachedLocationLimit();
          if (limit != null) {
            const activeCount = get().getActiveLocations().length;
            if (activeCount >= limit) {
              set({ error: `Your plan is limited to ${limit} location${limit === 1 ? '' : 's'}. Upgrade to add more.` });
              return null;
            }
          }
        }
        try {
          const res = await fetch(LOCATIONS_URL, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(toApiPayload(data)),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          const body = await res.json();
          const location = fromApiLocation(body.location);
          // Refresh the full list
          await get().fetchLocations();
          return location;
        } catch (error) {
          console.error('[Locations] Create failed:', error);
          set({ error: error.message });
          return null;
        }
      },

      /**
       * Update an existing location via API.
       * @param {number} id - Location ID to update
       * @param {Object} data - Fields to update { name, address, phone, timezone, is_active }
       * @returns {Promise<Object|null>} The updated location or null on failure
       */
      updateLocation: async (id, data) => {
        try {
          const res = await fetch(`${LOCATIONS_URL}/${id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(toApiPayload(data)),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          const body = await res.json();
          const location = fromApiLocation(body.location);
          // Refresh the full list
          await get().fetchLocations();
          return location;
        } catch (error) {
          console.error('[Locations] Update failed:', error);
          set({ error: error.message });
          return null;
        }
      },

      /**
       * Soft-delete (deactivate) a location via API.
       * @param {number} id - Location ID to deactivate
       * @returns {Promise<boolean>} true if successful, false on failure
       */
      deleteLocation: async (id) => {
        try {
          const res = await fetch(`${LOCATIONS_URL}/${id}`, {
            method: 'DELETE',
            headers: authHeaders(),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          // If the deleted location was selected, switch to default
          const { currentLocationId } = get();
          if (currentLocationId === id) {
            set({ currentLocationId: 1 });
          }
          // Refresh the full list
          await get().fetchLocations();
          return true;
        } catch (error) {
          console.error('[Locations] Delete failed:', error);
          set({ error: error.message });
          return false;
        }
      },
    }),
    {
      name: 'fire-pos-location',
      partialize: (state) => ({
        currentLocationId: state.currentLocationId,
      }),
      // v1 (ticket 9095): builds before it auto-switched to a tenants.locations
      // UUID and persisted it here, which keeps the floor plan empty after an
      // update. Data scope is an integer location id (default 1) until the
      // server aligns tenant location ids, so anything else resets to 1.
      version: 1,
      migrate: (persisted) => {
        const id = persisted?.currentLocationId;
        return { ...persisted, currentLocationId: Number.isInteger(id) && id > 0 ? id : 1 };
      },
    }
  )
);

export default useLocationStore;
